import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import FilterBar from '../components/ui/FilterBar';
import { simpleApiService } from '../services/simpleApi';
import { apiService } from '../services/api';
import { formatDate, formatDateOnly, formatTimeOnly } from '../lib/utils';
import { useDebounce } from '../hooks/useDebounce';
import { useToastNotifications } from '../components/ui/Toast';
import { useConfirmModal } from '../components/ui/ConfirmModal';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/Select';
import ConfirmModal from '../components/ui/ConfirmModal';
// import { useSmartRefresh } from '../hooks/useSmartRefresh';
import { 
  Users as UsersIcon, 
  //Plus, 
  Edit, 
  Eye,
  Copy,
  UserCheck,
  UserX,
  Clock,
  Wifi,
  RefreshCw,
  Trash2,
  Globe,
  Zap,
  Monitor,
  X,
  Repeat,
  Package,
  Calendar
} from 'lucide-react';

// Importar modais
import ClientViewModal from '../components/modals/ClientViewModal';
import ClientEditModal from '../components/modals/ClientEditModal';
import ClientRenewModal from '../components/modals/ClientRenewModal';
import ClientChangePlanModal from '../components/modals/ClientChangePlanModal';
import ClientExtendModal from '../components/modals/ClientExtendModal';
import ClientDetailsModal from '../components/modals/ClientDetailsModal';
import AddScreensModal from '../components/modals/AddScreensModal';
import RenewalSuccessModal from '../components/modals/RenewalSuccessModal';
import TrialCreatedModal from '../components/modals/TrialCreatedModal';
import { useQuickTrialHttp } from '../hooks/useQuickTrialHttp';

interface Client {
  id: string;
  username: string;
  password: string;
  member_id: number;
  package_id?: number;
  exp_date?: number;
  max_connections: number;
  is_trial: number;
  system: number;
  enabled: number;
  p2p_id?: number;
  p2p_status?: string;
  reseller_notes?: string;
  created_at: string;
  updated_at: string;
  member?: {
    id: number;
    username: string;
    email: string;
    status: number;
  };
  package?: {
    id: number;
    package_name: string;
    official_credits: number;
    official_duration: number;
  };
}

const toSafeNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizeClientStats = (raw: any) => ({
  total: toSafeNumber(raw?.total),
  active: toSafeNumber(raw?.active),
  expiring: toSafeNumber(raw?.expiring),
  trial: toSafeNumber(raw?.trial),
  iptv_active: toSafeNumber(raw?.iptv_active),
  p2p_active: toSafeNumber(raw?.p2p_active)
});

const formatCount = (value: unknown): string => toSafeNumber(value).toLocaleString();

/** ID estável em string (evita Set/has falhar entre number vs string vindos da API). */
const clientRowId = (c: Client): string => String((c as any).id);

/** Concorrência na fila cliente (seleção manual e remoção por filtro). */
const LINE_DELETE_CONCURRENCY = 8;
/** Pausa extra entre exclusões na fila cliente (rate limit XUI). */
const LINE_DELETE_QUEUE_GAP_MS = 0;
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * exp_date pode vir em segundos, ms ou string; comparações usam sempre segundos Unix.
 */
const getExpSeconds = (raw: unknown): number => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n < 1e12 ? Math.floor(n) : Math.floor(n / 1000);
};

/** Valor seguro para formatDate (aceita segundos ou ms). */
const expDateForDisplay = (raw: unknown): number | string | null => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n < 1e12 ? n * 1000 : n;
};



const Clients: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  // Alguns backends/consultas podem retornar itens nulos no array.
  // Filtramos aqui para evitar que o render exploda e caia no ErrorBoundary.
  const safeClients = useMemo(
    () => (Array.isArray(clients) ? clients : []).filter((c): c is Client => {
      if (!c || typeof c !== 'object') return false;
      const maybeId = (c as any).id;
      return maybeId !== null && maybeId !== undefined && String(maybeId).trim() !== '';
    }),
    [clients]
  );
  const clientsByRowId = useMemo(() => {
    const m = new Map<string, Client>();
    safeClients.forEach((c) => m.set(clientRowId(c), c));
    return m;
  }, [safeClients]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [clientStats, setClientStats] = useState({
    total: 0,
    active: 0,
    expiring: 0,
    trial: 0,
    iptv_active: 0,
    p2p_active: 0
  });

  // Hooks para notificações modernas
  const { showSuccess, showError, showWarning } = useToastNotifications();
  const { showConfirm, ConfirmModalComponent } = useConfirmModal();
  const {
    generatingTrial,
    lastGeneratedTrial,
    setLastGeneratedTrial,
    handleGenerateTestAutomations
  } = useQuickTrialHttp();

  /** Evita recriar fetchClients quando só a referência do toast muda (re-render a cada tecla na busca). */
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  /** Após o 1º carregamento da lista, nunca mais usar isLoading=true (evita “reload” inteiro ao limpar busca). */
  const initialClientsLoadDoneRef = useRef(false);
  /** Descarta respostas de requisições antigas quando o usuário digita rápido nos filtros. */
  const fetchClientsSeqRef = useRef(0);

  // Debounce para busca (500ms de delay)
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const normalizedSearchTerm = useMemo(() => {
    const trimmed = (debouncedSearchTerm || '').trim();
    if (!trimmed) return '';
    // Remove caracteres que costumam acionar bloqueios/WAF em alguns ambientes.
    return trimmed.replace(/[^a-zA-Z0-9À-ÿ\s._\-@]/g, '');
  }, [debouncedSearchTerm]);
  const effectiveSearchTerm = normalizedSearchTerm.length >= 3 ? normalizedSearchTerm : '';
  
  // Filtros
  const [statusFilter, setStatusFilter] = useState('');
  const [packageFilter, setPackageFilter] = useState('');
  const [trialFilter, setTrialFilter] = useState('');
  const [systemFilter, setSystemFilter] = useState('');
  const [resellerFilter, setResellerFilter] = useState(''); // Filtro de revendedor
  const [expiringFilter, setExpiringFilter] = useState(''); // Filtro de expiração
  
  // Opções para filtros
  const [statusOptions, setStatusOptions] = useState<Array<{value: string, label: string}>>([]);
  const [packageOptions, setPackageOptions] = useState<Array<{value: string, label: string}>>([]);
  const [trialOptions, setTrialOptions] = useState<Array<{value: string, label: string}>>([]);
  const [systemOptions, setSystemOptions] = useState<Array<{value: string, label: string}>>([]);
  const [expiringOptions, setExpiringOptions] = useState<Array<{value: string, label: string}>>([]);

  // Estados dos modais
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isAddScreensModalOpen, setIsAddScreensModalOpen] = useState(false);
  const [isAddingScreens, setIsAddingScreens] = useState(false);
  const [renewalSuccessData, setRenewalSuccessData] = useState<{ client: any; renewalData: any } | null>(null);
  const [isRenewalSuccessModalOpen, setIsRenewalSuccessModalOpen] = useState(false);
  /** Quando true, a aba Clientes fala com o XUI remoto (mesmo flag do gerador de testes). */
  const [xuiIndependentEnabled, setXuiIndependentEnabled] = useState(false);

  // Estados para remoção em massa
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteFilterType, setBulkDeleteFilterType] = useState<
    'expired' | 'expired_trial' | 'expired_non_trial' | 'expired_days' | 'expired_days_trial' | 'trial'
  >('expired');
  const [bulkDeleteDaysExpired, setBulkDeleteDaysExpired] = useState<number>(30);
  const [bulkDeletePreview, setBulkDeletePreview] = useState<any>(null);
  const [isLoadingBulkDeletePreview, setIsLoadingBulkDeletePreview] = useState(false);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Seleção múltipla para remoção em massa
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);

  /** Fila: um DELETE /lines/:id por vez (igual ao botão Remover), com barra de progresso. */
  const [lineDeleteQueue, setLineDeleteQueue] = useState<{
    open: boolean;
    running: boolean;
    current: number;
    total: number;
    label: string;
    succeeded: number;
    failed: { id: string; message: string }[];
    truncated?: boolean;
    totalMatching?: number;
  } | null>(null);

  const toggleSelectClient = (clientId: string) => {
    setSelectedClientIds(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedClientIds.size === safeClients.length) {
      setSelectedClientIds(new Set());
    } else {
      setSelectedClientIds(new Set(safeClients.map(c => clientRowId(c))));
    }
  };

  const clearSelection = () => setSelectedClientIds(new Set());

  // Remover da seleção IDs que não estão mais na lista
  useEffect(() => {
    const clientIds = new Set(safeClients.map(c => clientRowId(c)));
    setSelectedClientIds(prev => {
      const next = new Set(prev);
      let changed = false;
      next.forEach(id => {
        if (!clientIds.has(id)) { next.delete(id); changed = true; }
      });
      return changed ? next : prev;
    });
  }, [safeClients]);

  // Flag do modo XUI independente (mesmo interruptor do gerador de testes)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getTrialGeneratorSettings();
        if (!cancelled) {
          setXuiIndependentEnabled(!!data?.data?.xuiIndependentEnabled);
        }
      } catch {
        if (!cancelled) setXuiIndependentEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchClients = useCallback(async () => {
    const seq = ++fetchClientsSeqRef.current;
    try {
      // Só a primeira carga usa skeleton completo (isLoading). Depois disso, só indicador na lista (isSearching).
      if (!initialClientsLoadDoneRef.current) {
        setIsLoading(true);
      } else {
        setIsSearching(true);
      }

      
      // Determinar ordenação e filtro de expiração com base no valor selecionado
      const isExpiringSort = expiringFilter === 'all_asc';
      const isExpiredFilter = expiringFilter === 'expired';
      const expiringDays = (!isExpiringSort && !isExpiredFilter && expiringFilter) ? expiringFilter : undefined;

      const options = {
        page: currentPage,
        per: itemsPerPage,
        order_by: (expiringFilter && !isExpiredFilter) ? 'exp_date' : 'created_at',
        order: (expiringFilter && !isExpiredFilter) ? 'asc' : 'desc',
        order_direction: (expiringFilter && !isExpiredFilter) ? 'asc' : 'desc',
        search: effectiveSearchTerm || undefined,
        status: statusFilter || undefined,
        package: packageFilter || undefined,
        trial: trialFilter || undefined,
        system: systemFilter || undefined,
        owner_id: resellerFilter || undefined,
        // 'expired' → passa como valor especial; 'all_asc' → só ordena, sem filtrar dias
        expiring: isExpiredFilter ? 'expired' : expiringDays
      };
      
      const response = await simpleApiService.getLines(options);

      if (seq !== fetchClientsSeqRef.current) return;

      if (Array.isArray(response.data)) {
        // Preservar os dados originais do backend sem alterações desnecessárias
        setClients(response.data);
        setTotalPages(response.pagination?.total_pages || 1);
        setTotalClients(response.pagination?.total || 0);
      } else {
        console.warn('Resposta inesperada em getLines (data não é array):', response);
        setClients([]);
        setTotalPages(1);
        setTotalClients(0);
      }
    } catch (error: any) {
      if (seq !== fetchClientsSeqRef.current) return;

      console.error('Erro ao carregar clientes:', error);

      const status = error?.response?.status;

      // 401: interceptor do axios pode já tratar sessão — evita toast “sem permissão” genérico
      if (status === 401) {
        // sem toast
      } else if (status === 403) {
        showErrorRef.current(
          'Não foi possível buscar',
          'A API recusou esta consulta (403). Pode ser rede, firewall ou permissão. Tente de novo em instantes ou verifique sua sessão.'
        );
      } else if (error?.message?.includes('localStorage')) {
        showErrorRef.current(
          'Erro de Armazenamento',
          'Seu navegador está bloqueando o armazenamento local. Por favor, verifique as configurações de privacidade.'
        );
      } else if (error?.code === 'ERR_NETWORK' || String(error?.message || '').includes('Network Error')) {
        showWarning(
          'Rede',
          'Falha de conexão ao buscar clientes. Verifique a internet e tente novamente.'
        );
      }

      setClients([]);
      setTotalPages(1);
      setTotalClients(0);
    } finally {
      if (seq === fetchClientsSeqRef.current) {
        setIsLoading(false);
        setIsSearching(false);
        initialClientsLoadDoneRef.current = true;
      }
    }
  }, [currentPage, itemsPerPage, effectiveSearchTerm, statusFilter, packageFilter, trialFilter, systemFilter, resellerFilter, expiringFilter, showWarning]);

  // Carregar opções dos filtros
  const loadFilterOptions = useCallback(async () => {
    try {
      setStatusOptions([
        { value: '1', label: 'Ativo' },
        { value: 'blocked', label: 'Bloqueado' },
        { value: '0', label: 'Inativo (expirado)' }
      ]);

      setTrialOptions([
        { value: '1', label: 'Trial' },
        { value: '0', label: 'Oficial' }
      ]);

      setSystemOptions([
        { value: '1', label: 'IPTV' },
        { value: '2', label: 'P2P' }
      ]);

      setExpiringOptions([
        { value: 'expired', label: '🔴 Expirados' },
        { value: '1',       label: '🚨 Vencem hoje' },
        { value: '3',       label: '⚠️ Próximos 3 dias' },
        { value: '7',       label: '🟡 Próximos 7 dias' },
        { value: '15',      label: '🟠 Próximos 15 dias' },
        { value: '30',      label: '📅 Próximos 30 dias' },
        { value: '60',      label: '📆 Próximos 60 dias' },
        { value: 'all_asc', label: '↑ Todos por vencimento (mais próximo primeiro)' },
      ]);

      try {
        const res = (await apiService.getPackages(1, 500)) as {
          data?: Array<{ id: number; package_name?: string; display_name?: string; name?: string; is_trial?: number }>;
        };
        const rows = Array.isArray(res?.data) ? res.data : [];
        const official = rows.filter((p) => Number(p.is_trial) !== 1);
        setPackageOptions(
          official.map((p) => ({
            value: String(p.id),
            label: p.display_name || p.package_name || p.name || `Pacote #${p.id}`
          }))
        );
      } catch (pkgErr) {
        console.warn('Não foi possível carregar pacotes para o filtro:', pkgErr);
        setPackageOptions([]);
      }
    } catch (error) {
      console.error('Erro ao carregar opções dos filtros:', error);
    }
  }, []);

  // Função para buscar estatísticas dos clientes
  const fetchClientStats = useCallback(async () => {
    try {
      const stats = await simpleApiService.getLinesStats({
        owner_id: resellerFilter || undefined
      });
      setClientStats(normalizeClientStats(stats));
    } catch (error) {
      console.error('Erro ao buscar estatísticas dos clientes:', error);
      setClientStats({
        total: 0,
        active: 0,
        expiring: 0,
        trial: 0,
        iptv_active: 0,
        p2p_active: 0
      });
    }
  }, [resellerFilter]);

  const runLineDeleteQueue = async (
    ids: string[],
    meta?: { truncated?: boolean; totalMatching?: number }
  ) => {
    if (ids.length === 0) {
      showWarning('Nada a remover', 'Nenhuma linha na fila.');
      return;
    }
    const failed: { id: string; message: string }[] = [];
    let succeeded = 0;
    let done = 0;
    setLineDeleteQueue({
      open: true,
      running: true,
      current: 0,
      total: ids.length,
      label: '',
      succeeded: 0,
      failed: [],
      truncated: meta?.truncated,
      totalMatching: meta?.totalMatching
    });

    const concurrency = Math.min(LINE_DELETE_CONCURRENCY, ids.length);
    let nextIndex = 0;

    const worker = async () => {
      while (true) {
        const i = nextIndex++;
        if (i >= ids.length) break;
        const id = ids[i];
        const client = clientsByRowId.get(id);
        const label = client?.username || id;
        try {
          await apiService.deleteLine(id, { bulkQueue: true });
          succeeded++;
        } catch (e: any) {
          const message =
            e?.response?.data?.error ||
            e?.response?.data?.message ||
            e?.message ||
            'Erro ao excluir';
          failed.push({ id, message: String(message) });
        }
        done++;
        const currentDone = done;
        const currentSucceeded = succeeded;
        const currentFailed = [...failed];
        setLineDeleteQueue((prev) =>
          prev
            ? {
                ...prev,
                current: currentDone,
                label,
                succeeded: currentSucceeded,
                failed: currentFailed
              }
            : null
        );
        if (LINE_DELETE_QUEUE_GAP_MS > 0 && i < ids.length - 1) {
          await sleep(LINE_DELETE_QUEUE_GAP_MS);
        }
      }
    };

    await Promise.all(Array.from({ length: concurrency }, () => worker()));

    setLineDeleteQueue((prev) =>
      prev
        ? {
            ...prev,
            running: false,
            succeeded,
            failed
          }
        : null
    );
    simpleApiService.invalidateLines();
    fetchClients();
    fetchClientStats();
    clearSelection();
    if (failed.length === 0) {
      showSuccess('Concluído', `${succeeded} linha(s) removida(s).`);
    } else {
      showWarning(
        'Concluído com falhas',
        `${succeeded} removida(s), ${failed.length} falharam.`
      );
    }
  };

  // Opções de filtro: uma vez (não acoplar à busca / paginação).
  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Estatísticas: só quando o filtro de revendedor mudar — evita rajada de chamadas a cada tecla na busca.
  useEffect(() => {
    fetchClientStats();
  }, [fetchClientStats]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSearch = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    setCurrentPage(1);
    fetchClients();
  };

  const clearFilters = () => {
    setStatusFilter('');
    setPackageFilter('');
    setTrialFilter('');
    setSystemFilter('');
    setResellerFilter('');
    setExpiringFilter('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleBlockClient = async (clientId: string) => {
    try {
      await simpleApiService.blockLine(clientId);
      simpleApiService.invalidateLines();
      fetchClients();
      fetchClientStats();
      showSuccess('Linha bloqueada', 'O cliente foi bloqueado com sucesso.');
    } catch (error: any) {
      console.error('Erro ao bloquear cliente:', error);
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Não foi possível bloquear a linha.';
      showError('Erro ao bloquear', msg);
    }
  };

  const handleUnblockClient = async (clientId: string) => {
    try {
      await simpleApiService.unblockLine(clientId);
      simpleApiService.invalidateLines();
      fetchClients();
      fetchClientStats();
      showSuccess('Linha desbloqueada', 'O cliente foi desbloqueado com sucesso.');
    } catch (error: any) {
      console.error('Erro ao desbloquear cliente:', error);
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Não foi possível desbloquear a linha.';
      showError('Erro ao desbloquear', msg);
    }
  };

  const handleResyncP2p = async (clientId: string) => {
    await showConfirm(
      'Ressincronizar P2P',
      'Tem certeza que deseja ressincronizar este cliente P2P?',
      {
        type: 'warning',
        confirmText: 'Ressincronizar',
        onConfirm: async () => {
          try {
            await apiService.resyncUserP2p(clientId);
            // Invalidate cache and refetch
            simpleApiService.invalidateLines();
            fetchClients();
            fetchClientStats();
            showSuccess(
              'P2P Ressincronizado',
              'Cliente P2P ressincronizado com sucesso!'
            );
          } catch (error) {
            console.error('Erro ao ressincronizar cliente P2P:', error);
            showError(
              'Erro na Ressincronização',
              'Erro ao ressincronizar cliente P2P'
            );
          }
        }
      }
    );
  };

  const handleDeleteClient = async (clientId: string, username: string) => {
    await showConfirm(
      'Excluir Linha',
      `Tem certeza que deseja excluir a linha "${username}"?\n\nEsta ação é irreversível.`,
      {
        type: 'danger',
        confirmText: 'Excluir',
        onConfirm: async () => {
          try {
            await apiService.deleteLine(clientId);
            simpleApiService.invalidateLines();
            fetchClients();
            fetchClientStats();
            showSuccess(
              'Linha Excluída',
              `A linha "${username}" foi excluída com sucesso.`
            );
          } catch (error) {
            console.error('Erro ao excluir cliente:', error);
            showError(
              'Erro ao Excluir',
              'Erro ao excluir cliente'
            );
          }
        }
      }
    );
  };

  // Função para fazer preview da remoção em massa
  const handlePreviewBulkDelete = async () => {
    try {
      setIsLoadingBulkDeletePreview(true);
      const response = await apiService.getBulkDeleteLinesPreview(
        bulkDeleteFilterType,
        bulkDeleteDaysExpired,
        false // onlyTrial não é necessário pois os filtros já são específicos
      );
      
      if (response.success) {
        setBulkDeletePreview(response.data);
      } else {
        showError('Erro ao fazer preview', response.error || 'Erro ao fazer preview de remoção');
      }
    } catch (error: any) {
      console.error('Erro ao fazer preview:', error);
      showError('Erro ao fazer preview', error.response?.data?.error || 'Erro ao fazer preview de remoção');
    } finally {
      setIsLoadingBulkDeletePreview(false);
    }
  };

  // Remoção por filtro: mesma lógica do modo select (DELETE /lines/:id um a um)
  const handleExecuteBulkDelete = async () => {
    try {
      setIsDeletingBulk(true);
      setShowBulkDeleteConfirm(false);
      setShowBulkDeleteModal(false);
      setBulkDeletePreview(null);

      const idsRes = await apiService.getBulkDeleteLineIds(
        bulkDeleteFilterType,
        bulkDeleteDaysExpired,
        false
      );

      if (!idsRes?.success) {
        showError('Erro ao remover linhas', idsRes?.error || 'Falha ao listar linhas do filtro.');
        return;
      }

      const ids = (idsRes.data?.ids || []).map((id: number) => String(id));
      if (ids.length === 0) {
        showWarning('Nenhuma linha', 'Nenhuma linha encontrada para remover com este filtro.');
        return;
      }

      await runLineDeleteQueue(ids, {
        truncated: idsRes.data?.truncated,
        totalMatching: idsRes.data?.totalMatching
      });
    } catch (error: any) {
      console.error('Erro ao remover linhas em massa:', error);
      setLineDeleteQueue(null);
      showError(
        'Erro ao remover linhas',
        error?.response?.data?.error || error?.message || 'Erro na remoção em massa'
      );
    } finally {
      setIsDeletingBulk(false);
    }
  };

  const handleBulkDeleteSelected = async () => {
    const ids = Array.from(selectedClientIds);
    if (ids.length === 0) return;
    await showConfirm(
      'Excluir Linhas Selecionadas',
      `Tem certeza que deseja excluir ${ids.length} linha(s) selecionada(s)?\n\nEsta ação é irreversível.`,
      {
        type: 'danger',
        confirmText: 'Excluir',
        onConfirm: async () => {
          try {
            setIsDeletingSelected(true);
            await runLineDeleteQueue(ids);
          } catch (error) {
            console.error('Erro ao excluir linhas selecionadas:', error);
            showError('Erro ao Excluir', 'Erro ao excluir linhas selecionadas');
          } finally {
            setIsDeletingSelected(false);
          }
        }
      }
    );
  };

  // Resetar preview quando mudar filtros
  useEffect(() => {
    if (showBulkDeleteModal) {
      setBulkDeletePreview(null);
    }
  }, [bulkDeleteFilterType, bulkDeleteDaysExpired, showBulkDeleteModal]);

  const handleQuickRenew = async (client: Client) => {
    // Calcular custo: 1 cr por conexão/mês
    // Exemplo: 2 conexões = 2cr, 3 conexões = 3cr
    const connections = client.max_connections || 1;
    const cost = connections * 1; // 1 mês * 1cr por conexão
    
    const confirmTitle = client.is_trial === 1 ? 'Renovar Teste' : 'Renovar Cliente';
    const confirmMessage = client.is_trial === 1 
      ? `Renovar este TESTE por 1 mês?\n\nConexões: ${connections}\nCusto: ${cost} crédito(s)\n\nO cliente será convertido para conta normal.`
      : `Renovar este cliente por 1 mês?\n\nConexões: ${connections}\nCusto: ${cost} crédito(s)`;
    
    await showConfirm(
      confirmTitle,
      confirmMessage,
      {
        type: 'info',
        confirmText: 'Confirmar Renovação',
        onConfirm: async () => {
          try {
            const response = await apiService.quickRenewClient(client.id);
            if (response.success) {
              const rd = response.data ?? response;
              const renewedLine = rd?.line || rd || {};
              const passwordFromApi =
                rd?.password ||
                renewedLine?.password ||
                client.password ||
                '';
              const expRaw = rd.new_exp_date ?? rd.exp_date ?? renewedLine?.exp_date;
              const unix = typeof expRaw === 'number'
                ? expRaw
                : Math.floor(new Date(expRaw).getTime() / 1000);
              const renewalData = {
                new_exp_date: unix,
                new_exp_date_formatted: rd.new_exp_date_formatted ?? new Date(unix * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
                cost_paid: rd.cost_paid ?? 0
              };
              // Atualizar créditos da sidebar quando modo XUI independente debita no remoto
              if (rd.remaining_credits != null && Number.isFinite(Number(rd.remaining_credits))) {
                try {
                  const { useAuthStore } = await import('../store/authStore');
                  const current = useAuthStore.getState().user;
                  if (current) {
                    useAuthStore.getState().setUser({
                      ...current,
                      credits: Number(rd.remaining_credits)
                    });
                  }
                } catch (_) {}
              }
              const clientForModal = {
                id: String(client.id),
                username: client.username ?? '',
                password: passwordFromApi,
                email: (client.member as any)?.email ?? '',
                whatsapp: (client as any).whatsapp ?? (client as any).contact ?? '',
                contact: (client as any).whatsapp ?? (client as any).contact ?? '',
                max_connections: client.max_connections ?? 1,
                maxConnections: client.max_connections ?? 1,
                is_trial: renewedLine?.is_trial ?? client.is_trial ?? 0,
                system: client.system ?? 1,
                package_name: rd.package_name || (client.package as any)?.package_name || '1 mês',
                packageName: rd.package_name || (client.package as any)?.package_name || '1 mês'
              };
              setRenewalSuccessData({ client: clientForModal, renewalData });
              setIsRenewalSuccessModalOpen(true);
            }
          } catch (error: any) {
            const errorData = error.response?.data;
            if (errorData?.required != null && errorData?.available != null) {
              showError('Créditos Insuficientes', `Necessário: ${errorData.required} créditos\nDisponível: ${errorData.available} créditos`);
            } else {
              showError('Erro na Renovação', errorData?.error || 'Erro desconhecido');
            }
          }
        }
      }
    );
  };

  const handleAddScreens = async (client: Client) => {
    // Verificar se já tem o máximo de telas
    if (client.max_connections >= 3) {
      showWarning(
        'Limite Máximo Atingido',
        `Este cliente já possui ${client.max_connections} tela(s). O limite máximo é de 3 telas por linha.`
      );
      return;
    }

    // Abrir modal de adicionar telas
    setSelectedClient(client);
    setIsAddScreensModalOpen(true);
  };

  const handleConfirmAddScreens = async (screensToAdd: number) => {
    if (!selectedClient) return;

    setIsAddingScreens(true);
    try {
      const response = await apiService.addScreensToLine(selectedClient.id, screensToAdd);
      
      if (response.success) {
        showSuccess(
          'Telas Adicionadas',
          `Cliente: ${selectedClient.username}\nTelas: ${response.data.old_screens} → ${response.data.new_screens}\nCusto: ${response.data.cost_paid} crédito(s)`
        );
        
        // Atualizar dados
        simpleApiService.invalidateLines();
        fetchClients();
        fetchClientStats();
        
        // Fechar modal
        setIsAddScreensModalOpen(false);
        setSelectedClient(null);
      }
    } catch (error: any) {
      console.error('Erro ao adicionar telas:', error);
      
      const errorData = error.response?.data;
      if (errorData?.required && errorData?.available !== undefined) {
        showError(
          'Créditos Insuficientes',
          `Necessário: ${errorData.required} créditos\nDisponível: ${errorData.available} créditos`
        );
      } else {
        showError(
          'Erro ao Adicionar Telas',
          errorData?.error || 'Erro desconhecido'
        );
      }
    } finally {
      setIsAddingScreens(false);
    }
  };

  // Funções para abrir modais
  const openViewModal = (client: Client) => {
    setSelectedClient(client);
    setIsViewModalOpen(true);
  };

  const openEditModal = (client: Client) => {
    setSelectedClient(client);
    setIsEditModalOpen(true);
  };

  const openRenewModal = (client: Client) => {
    setSelectedClient(client);
    setIsRenewModalOpen(true);
  };

  const openChangePlanModal = (client: Client) => {
    setSelectedClient(client);
    setIsChangePlanModalOpen(true);
  };


  const openExtendModal = (client: Client) => {
    setSelectedClient(client);
    setIsExtendModalOpen(true);
  };

  const openDetailsModal = (client: Client) => {
    setSelectedClient(client);
    setIsDetailsModalOpen(true);
  };

  // Funções para fechar modais
  const closeAllModals = () => {
    setIsViewModalOpen(false);
    setIsEditModalOpen(false);
    setIsRenewModalOpen(false);
    setIsChangePlanModalOpen(false);
    setIsExtendModalOpen(false);
    setIsDetailsModalOpen(false);
    setIsAddScreensModalOpen(false);
    setSelectedClient(null);
  };

  // Funções de gerenciamento (removidas - agora usando modais integrados)

  const handleChangePlan = async (clientId: string, packageId: number) => {
    try {
      await apiService.changePlan(clientId, packageId);
      simpleApiService.invalidateLines();
      fetchClients(); // Recarregar lista
      fetchClientStats(); // Recarregar estatísticas
    } catch (error) {
      console.error('Erro ao alterar plano:', error);
      throw error;
    }
  };



  const handleChangeSystem = async (clientId: string, newSystem: number) => {
    await simpleApiService.changeUserSystem(clientId, { system: newSystem });
    simpleApiService.invalidateLines();
    fetchClients();
    fetchClientStats();
  };

  const handleToggleSystem = async (client: Client) => {
    const newSystem = client.system === 1 ? 2 : 1;
    const msg = newSystem === 2
      ? 'Alterar este cliente para P2P? A linha IPTV será bloqueada e o usuário P2P será criado na hora.'
      : 'Alterar este cliente para IPTV? A linha P2P será bloqueada.';
    if (!window.confirm(msg)) return;
    try {
      await handleChangeSystem(client.id.toString(), newSystem);
      closeAllModals();
      showSuccess('Sistema alterado', newSystem === 2 ? 'Cliente alterado para P2P.' : 'Cliente alterado para IPTV.');
    } catch (error) {
      console.error('Erro ao alterar sistema:', error);
      showError('Erro ao alterar sistema', (error as any)?.response?.data?.error || (error as Error)?.message || 'Tente novamente.');
    }
  };

  const isExpired = (expDate: unknown) => {
    const sec = getExpSeconds(expDate);
    if (!sec) return false;
    return sec < Math.floor(Date.now() / 1000);
  };

  const getDaysUntilExpiration = (expDate: unknown) => {
    const sec = getExpSeconds(expDate);
    if (!sec) return null;
    const now = Math.floor(Date.now() / 1000);
    const diff = sec - now;
    return Math.ceil(diff / (24 * 60 * 60));
  };


  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
            <p className="text-gray-600">Gerenciar linhas dos clientes finais</p>
          </div>
        </div>

        {/* Skeleton para estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                    <div className="h-8 bg-gray-200 rounded w-12"></div>
                  </div>
                  <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Skeleton para filtros */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-pulse">
          <div className="space-y-4">
            <div className="h-11 bg-gray-200 rounded"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Skeleton para tabela */}
        <Card className="shadow-sm">
          <CardHeader className="bg-gray-50 border-b">
            <div className="h-6 bg-gray-200 rounded w-32"></div>
            <div className="h-4 bg-gray-200 rounded w-48 mt-2"></div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="space-y-4 p-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="h-4 bg-gray-200 rounded flex-1"></div>
                  <div className="h-4 bg-gray-200 rounded flex-1"></div>
                  <div className="h-4 bg-gray-200 rounded flex-1"></div>
                  <div className="h-6 bg-gray-200 rounded w-20"></div>
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-200 rounded w-12"></div>
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                  <div className="h-8 bg-gray-200 rounded w-24"></div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="page-header-mobile flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Clientes</h1>
            {xuiIndependentEnabled && (
              <span className="inline-flex items-center rounded-full bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-1 border border-purple-200">
                XUI independente
              </span>
            )}
          </div>
          <p className="text-sm sm:text-base text-gray-600">
            {xuiIndependentEnabled
              ? 'Gerenciar linhas no painel XUI remoto (domínio/token próprios)'
              : 'Gerenciar linhas dos clientes finais'}
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto flex-wrap">
          <Button
            variant="outline"
            onClick={handleGenerateTestAutomations}
            disabled={generatingTrial}
            className="flex items-center justify-center gap-2"
            title="Gerar teste rápido com credenciais"
          >
            {generatingTrial ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span className="hidden sm:inline">Gerando...</span>
                <span className="sm:hidden">...</span>
              </>
            ) : (
              <>
                <Zap className="h-4 w-4" />
                <span className="hidden sm:inline">Teste Rápido HTTP</span>
                <span className="sm:hidden">Teste rápido</span>
              </>
            )}
          </Button>
          {selectedClientIds.size > 0 && (
            <Button
              variant="outline"
              className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
              onClick={handleBulkDeleteSelected}
              disabled={isDeletingSelected}
            >
              {isDeletingSelected ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Remover {selectedClientIds.size} selecionado(s)</span>
              <span className="sm:hidden">Remover {selectedClientIds.size}</span>
            </Button>
          )}
          <Button 
            variant="outline"
            className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
            onClick={() => setShowBulkDeleteModal(true)}
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Remover em Massa</span>
            <span className="sm:hidden">Remover</span>
          </Button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 lg:gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm lg:text-lg font-medium text-blue-700 truncate">Total de Clientes</p>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-blue-900">{formatCount(clientStats.total)}</p>
              </div>
              <div className="p-2 lg:p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-lg flex-shrink-0">
                <UsersIcon className="h-4 w-4 lg:h-6 lg:w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm lg:text-lg font-medium text-green-700 truncate">Total de Clientes Ativos</p>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-green-900">{formatCount(clientStats.active)}</p>
              </div>
              <div className="p-2 lg:p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-full shadow-lg flex-shrink-0">
                <UserCheck className="h-4 w-4 lg:h-6 lg:w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm lg:text-lg font-medium text-orange-700 truncate">IPTV Ativos</p>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-orange-900">{formatCount(clientStats.iptv_active)}</p>
              </div>
              <div className="p-2 lg:p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full shadow-lg flex-shrink-0">
                <Wifi className="h-4 w-4 lg:h-6 lg:w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm lg:text-lg font-medium text-purple-700 truncate">
                  {xuiIndependentEnabled ? 'Remoto (XUI)' : 'P2P Ativos'}
                </p>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-purple-900">
                  {formatCount(xuiIndependentEnabled ? clientStats.total : clientStats.p2p_active)}
                </p>
              </div>
              <div className="p-2 lg:p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full shadow-lg flex-shrink-0">
                <Wifi className="h-4 w-4 lg:h-6 lg:w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm lg:text-lg font-medium text-yellow-700 truncate">15 dias p/expirar</p>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-yellow-900">{formatCount(clientStats.expiring)}</p>
              </div>
              <div className="p-2 lg:p-3 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full shadow-lg flex-shrink-0">
                <Clock className="h-4 w-4 lg:h-6 lg:w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm lg:text-lg font-medium text-indigo-700 truncate">Trial</p>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-indigo-900">{formatCount(clientStats.trial)}</p>
              </div>
              <div className="p-2 lg:p-3 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full shadow-lg flex-shrink-0">
                <Wifi className="h-4 w-4 lg:h-6 lg:w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e Busca */}
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSearchSubmit={() => handleSearch()}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        statusOptions={statusOptions}
        packageFilter={packageFilter}
        onPackageFilterChange={setPackageFilter}
        packageOptions={packageOptions}
        packageFilterLabel="Pacote"
        trialFilter={trialFilter}
        onTrialFilterChange={setTrialFilter}
        trialOptions={trialOptions}
        systemFilter={systemFilter}
        onSystemFilterChange={setSystemFilter}
        systemOptions={systemOptions}
        resellerFilter={resellerFilter}
        onResellerFilterChange={setResellerFilter}
        expiringFilter={expiringFilter}
        onExpiringFilterChange={setExpiringFilter}
        expiringOptions={expiringOptions}
        onClearFilters={clearFilters}
        showAdvancedFilters={true}
      />

      {/* Lista de Clientes */}
      <Card className="shadow-sm">
        <CardHeader className="bg-gray-50 border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                Lista de Clientes
                {isSearching && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                )}
              </CardTitle>
              <CardDescription className="mt-1">
                {formatCount(totalClients)} clientes encontrados
                {isSearching && " (buscando...)"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2 lg:p-4 w-10">
                    <input
                      type="checkbox"
                      checked={safeClients.length > 0 && selectedClientIds.size === safeClients.length}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      title="Selecionar todos"
                    />
                  </th>
                  <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg">Username</th>
                  <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg hidden sm:table-cell">Proprietário</th>
                  <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg hidden md:table-cell">Pacote</th>
                  <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg">Status</th>
                  <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg hidden lg:table-cell">Sistema</th>
                  <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg hidden xl:table-cell">Notas</th>
                  <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg hidden md:table-cell">Expiração</th>
                  <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg hidden xl:table-cell">Conexões</th>
                  <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg hidden lg:table-cell">Tipo</th>
                  <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg hidden xl:table-cell">Criado em</th>
                  <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {safeClients.map((client, index) => {
                  const rowId = clientRowId(client);
                  const daysUntilExp = getDaysUntilExpiration(client.exp_date);
                  const expired = isExpired(client.exp_date);
                  
                  return (
                    <tr key={rowId} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                      <td className="p-2 lg:p-4 checkbox-col">
                        <input
                          type="checkbox"
                          checked={selectedClientIds.has(rowId)}
                          onChange={() => toggleSelectClient(rowId)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          title="Selecionar"
                        />
                      </td>
                      <td className="p-2 lg:p-4">
                        <div className="space-y-1">
                          <p className="font-semibold text-gray-900 text-sm lg:text-base">{client.username}</p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs lg:text-sm text-gray-500">
                              Senha:{' '}
                              <span className="font-mono text-gray-700 select-all">
                                {client.password || 'N/A'}
                              </span>
                            </p>
                            {!!client.password && (
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(client.password);
                                    showSuccess('Copiado', 'Senha copiada para a área de transferência');
                                  } catch (_) {
                                    showError('Erro ao copiar', 'Não foi possível copiar a senha');
                                  }
                                }}
                                className="p-1 rounded hover:bg-gray-100"
                                title="Copiar senha"
                              >
                                <Copy className="h-3 w-3 text-gray-500" />
                              </button>
                            )}
                          </div>
                          {/* Telefones estreitos: proprietário, pacote, notas */}
                          <div className="sm:hidden space-y-1 mt-1">
                            {client.member && (
                              <p className="text-xs text-gray-600">Proprietário: <span className="font-medium">{client.member.username}</span></p>
                            )}
                            {client.package && (
                              <p className="text-xs text-gray-600">Pacote: <span className="font-medium">{client.package.package_name}</span></p>
                            )}
                            {client.reseller_notes && (
                              <p className="text-xs text-gray-600 italic line-clamp-2" title={client.reseller_notes}>
                                Nota: {client.reseller_notes}
                              </p>
                            )}
                          </div>
                          {/* Vencimento: visível em telas &lt; md (inclui mobile em landscape / tablets onde a coluna da tabela ainda não entra) */}
                          <div className="md:hidden space-y-1 mt-1">
                            {client.exp_date ? (
                              <p className={`text-xs font-medium ${
                                expired ? 'text-red-600' :
                                daysUntilExp !== null && daysUntilExp <= 7 ? 'text-yellow-600' :
                                'text-green-700'
                              }`}>
                                Venc.: {formatDate(expDateForDisplay(client.exp_date))}
                                {daysUntilExp !== null && (
                                  <span className="ml-1">({daysUntilExp <= 0 ? 'Expirado' : `${daysUntilExp}d`})</span>
                                )}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-400">Sem expiração</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-2 lg:p-4 hidden sm:table-cell">
                        {client.member ? (
                          <div className="space-y-1">
                            <p className="font-medium text-gray-900 text-sm lg:text-base">{client.member.username}</p>
                            <p className="text-xs lg:text-sm text-gray-500 truncate max-w-32">{client.member.email}</p>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm lg:text-base">N/A</span>
                        )}
                      </td>
                      <td className="p-2 lg:p-4 hidden md:table-cell">
                        {client.package ? (
                          <div className="space-y-1">
                            <p className="font-medium text-gray-900 text-sm lg:text-base">{client.package.package_name}</p>
                            <p className="text-xs lg:text-sm text-gray-500">R$ {client.package.official_credits}</p>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm lg:text-base">N/A</span>
                        )}
                      </td>
                      <td className="p-2 lg:p-4">
                        {(() => {
                          const isIptvActive = client.system === 1 && client.enabled === 1 && !expired;
                          const isP2pActive = client.system === 2 && String(client.p2p_status || '') === '1' && !expired;
                          const isActive = isIptvActive || isP2pActive;

                          const badgeClass = isActive
                            ? (isP2pActive
                                ? 'bg-purple-100 text-purple-800 border border-purple-200'
                                : 'bg-green-100 text-green-800 border border-green-200')
                            : 'bg-red-100 text-red-800 border border-red-200';

                          const dotClass = isActive
                            ? (isP2pActive ? 'bg-purple-500' : 'bg-green-500')
                            : 'bg-red-500';

                          const label = isActive
                            ? (isP2pActive ? 'Ativo P2P' : 'Ativo IPTV')
                            : (!expired ? 'Bloqueado' : 'Inativo');

                          return (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs lg:text-sm font-medium ${badgeClass}`}>
                              <div className={`w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full mr-1 lg:mr-1.5 ${dotClass}`}></div>
                              <span className="hidden sm:inline">{label}</span>
                              <span className="sm:hidden">{isActive ? 'Ativo' : (!expired ? 'Bloq.' : 'Inativo')}</span>
                            </span>
                          );
                        })()}
                      </td>
                      <td className="p-2 lg:p-4 hidden lg:table-cell">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs lg:text-sm font-medium ${
                          client.system === 1 
                            ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                            : 'bg-purple-100 text-purple-800 border border-purple-200'
                        }`}>
                          <div className={`w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full mr-1 lg:mr-1.5 ${
                            client.system === 1 ? 'bg-blue-500' : 'bg-purple-500'
                          }`}></div>
                          {client.system === 1 ? 'IPTV' : 'P2P'}
                        </span>
                      </td>
                      <td className="p-2 lg:p-4 hidden xl:table-cell max-w-[180px]">
                        <span className="text-xs lg:text-sm text-gray-700 line-clamp-2" title={client.reseller_notes || ''}>
                          {client.reseller_notes || '—'}
                        </span>
                      </td>
                      <td className="p-2 lg:p-4 hidden md:table-cell">
                        {client.exp_date ? (
                          <div className="space-y-1">
                            <p className="text-xs lg:text-sm font-medium text-gray-900">
                              {formatDate(expDateForDisplay(client.exp_date))}
                            </p>
                            {daysUntilExp !== null && (
                              <p className={`text-xs lg:text-sm font-medium ${
                                daysUntilExp <= 0 ? 'text-red-600' :
                                daysUntilExp <= 7 ? 'text-yellow-600' :
                                'text-green-600'
                              }`}>
                                {daysUntilExp <= 0 ? 'Expirado' : `${daysUntilExp} dias`}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs lg:text-sm">N/A</span>
                        )}
                      </td>
                      <td className="p-2 lg:p-4 hidden xl:table-cell">
                        <div className="flex items-center space-x-1">
                          <span className="text-xs lg:text-sm font-medium text-gray-900">0</span>
                          <span className="text-gray-400">/</span>
                          <span className="text-xs lg:text-sm text-gray-600">{client.max_connections}</span>
                        </div>
                      </td>
                      <td className="p-2 lg:p-4 hidden lg:table-cell">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs lg:text-sm font-medium ${
                          client.is_trial === 1 
                            ? 'bg-purple-100 text-purple-800 border border-purple-200' 
                            : 'bg-gray-100 text-gray-800 border border-gray-200'
                        }`}>
                          <div className={`w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full mr-1 lg:mr-1.5 ${
                            client.is_trial === 1 ? 'bg-purple-500' : 'bg-gray-500'
                          }`}></div>
                          {client.is_trial === 1 ? 'Trial' : 'Normal'}
                        </span>
                      </td>
                      <td className="p-2 lg:p-4 hidden xl:table-cell">
                        <div className="space-y-1">
                          {client.created_at ? (
                            <>
                              <p className="text-xs lg:text-sm font-medium text-gray-900">
                                {formatDateOnly(client.created_at)}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatTimeOnly(client.created_at)}
                              </p>
                            </>
                          ) : (
                            <span className="text-gray-400 text-xs lg:text-sm">N/A</span>
                          )}
                        </div>
                      </td>
                      <td className="p-2 lg:p-4">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openViewModal(client)}
                            title="Visualizar detalhes"
                            className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 p-0 hover:bg-blue-50 hover:border-blue-300"
                          >
                            <Eye className="h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openEditModal(client)}
                            title="Editar cliente"
                            className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 p-0 hover:bg-green-50 hover:border-green-300"
                          >
                            <Edit className="h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                          </Button>
                          {!xuiIndependentEnabled && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openRenewModal(client)}
                              title="Renovar cliente (escolher pacote)"
                              className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 p-0 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700"
                            >
                              <RefreshCw className="h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                            </Button>
                          )}
                          {!xuiIndependentEnabled && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openChangePlanModal(client)}
                              title="Alterar plano"
                              className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 p-0 hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700"
                            >
                              <Package className="h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openExtendModal(client)}
                            title="Editar vencimento"
                            className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 p-0 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700"
                          >
                            <Calendar className="h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleQuickRenew(client)}
                            title="Renovação Rápida (1 mês)"
                            className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 p-0 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600"
                          >
                            <Zap className="h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                          </Button>
                          {!xuiIndependentEnabled && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleAddScreens(client)}
                              title="Adicionar Telas"
                              disabled={client.max_connections >= 3}
                              className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 p-0 hover:bg-purple-50 hover:border-purple-300 hover:text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Monitor className="h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                            </Button>
                          )}
                          {!xuiIndependentEnabled && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleToggleSystem(client)}
                              title={client.system === 1 ? 'Alterar para P2P' : 'Alterar para IPTV'}
                              className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 p-0 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-600"
                            >
                              <Repeat className="h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                            </Button>
                          )}
                          {!xuiIndependentEnabled && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openDetailsModal(client)}
                              title="URLs de acesso"
                              className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 p-0 hover:bg-blue-50 hover:border-blue-300"
                            >
                              <Globe className="h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                            </Button>
                          )}
                          {(() => {
                            const isIptvActive = client.system === 1 && client.enabled === 1;
                            const isP2pActive = client.system === 2 && String(client.p2p_status || '') === '1';
                            const canBlock = isIptvActive || isP2pActive;
                            return canBlock ? (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleBlockClient(client.id)}
                              title="Bloquear cliente"
                              className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 p-0 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
                            >
                              <UserX className="h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleUnblockClient(client.id)}
                              title="Desbloquear cliente"
                              className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 p-0 hover:bg-green-50 hover:border-green-300 hover:text-green-600"
                            >
                              <UserCheck className="h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                            </Button>
                          );
                          })()}
                          {client.system === 2 && !xuiIndependentEnabled && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleResyncP2p(client.id)}
                              title="Ressincronizar P2P"
                              className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 p-0 hover:bg-purple-50 hover:border-purple-300 hover:text-purple-600"
                            >
                              <RefreshCw className="h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDeleteClient(client.id, client.username)}
                            title="Excluir linha"
                            className="h-6 w-6 lg:h-8 lg:w-8 p-0 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
                          >
                            <Trash2 className="h-3 w-3 lg:h-4 lg:w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Controles extras */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t bg-gray-50">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Por página:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value={10}>10</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="text-xs text-gray-500 text-center sm:text-right">
              Dica: use 50/100 no desktop para ver mais rápido
            </div>
          </div>

          {/* Paginação */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={totalClients}
            itemsPerPage={itemsPerPage}
          />
        </CardContent>
      </Card>

      {/* Modais */}
      <ClientViewModal
        client={selectedClient}
        isOpen={isViewModalOpen}
        onClose={closeAllModals}
      />

      <ClientEditModal
        user={selectedClient}
        isOpen={isEditModalOpen}
        onClose={closeAllModals}
        onSuccess={() => {
          closeAllModals();
          // Atualizar dados localmente sem reload da página
          simpleApiService.invalidateLines();
          fetchClients();
          fetchClientStats();
        }}
      />

      <ClientRenewModal
        user={selectedClient}
        isOpen={isRenewModalOpen}
        onClose={closeAllModals}
        onSuccess={() => {
          closeAllModals();
          simpleApiService.invalidateLines();
          fetchClients();
          fetchClientStats();
        }}
        onRenewalSuccess={({ client, renewalData }) => {
          setRenewalSuccessData({ client, renewalData });
          setIsRenewalSuccessModalOpen(true);
        }}
      />

      {renewalSuccessData && (
        <RenewalSuccessModal
          isOpen={isRenewalSuccessModalOpen}
          onClose={() => {
            setIsRenewalSuccessModalOpen(false);
            setRenewalSuccessData(null);
            simpleApiService.invalidateLines();
            fetchClients();
            fetchClientStats();
          }}
          client={renewalSuccessData.client}
          renewalData={renewalSuccessData.renewalData}
        />
      )}

      <TrialCreatedModal
        isOpen={!!lastGeneratedTrial}
        onClose={() => setLastGeneratedTrial(null)}
        trial={
          lastGeneratedTrial
            ? {
                username: lastGeneratedTrial.username || '',
                password: lastGeneratedTrial.password || '',
                expDate: lastGeneratedTrial.expDate,
                duration: lastGeneratedTrial.duration
              }
            : { username: '', password: '' }
        }
      />

      <ClientChangePlanModal
        client={selectedClient}
        isOpen={isChangePlanModalOpen}
        onClose={closeAllModals}
        onChangePlan={handleChangePlan}
      />


      <ClientExtendModal
        user={selectedClient}
        isOpen={isExtendModalOpen}
        onClose={closeAllModals}
        onSuccess={() => {
          closeAllModals();
          // Atualizar dados localmente sem reload da página
          simpleApiService.invalidateLines();
          fetchClients();
          fetchClientStats();
        }}
      />

      {selectedClient && (
        <ClientDetailsModal
          client={selectedClient}
          isOpen={isDetailsModalOpen}
          onClose={closeAllModals}
        />
      )}

      {/* Modal de Adicionar Telas */}
      {selectedClient && (
        <AddScreensModal
          isOpen={isAddScreensModalOpen}
          onClose={() => {
            setIsAddScreensModalOpen(false);
            setSelectedClient(null);
          }}
          onConfirm={handleConfirmAddScreens}
          client={selectedClient}
          isLoading={isAddingScreens}
        />
      )}

      {/* Modal de Remoção em Massa */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-50 rounded-full">
                    <Trash2 className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Remoção em Massa de Clientes</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Remova múltiplos clientes de uma vez usando filtros
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowBulkDeleteModal(false);
                    setBulkDeletePreview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Filtros */}
              <div className="space-y-4 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="filterType">Tipo de Filtro</Label>
                  <Select 
                    value={bulkDeleteFilterType} 
                    onValueChange={(value: any) => setBulkDeleteFilterType(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo de filtro" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expired">Todos os Expirados</SelectItem>
                      <SelectItem value="expired_trial">Expirados que são Teste</SelectItem>
                      <SelectItem value="expired_non_trial">Expirados que NÃO são Teste</SelectItem>
                      <SelectItem value="trial">Todos os Testes</SelectItem>
                      <SelectItem value="expired_days_trial">Expirados há X dias ou mais que são Teste</SelectItem>
                      <SelectItem value="expired_days">Expirados há X dias ou mais</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {bulkDeleteFilterType === 'expired' && 'Remove todos os clientes expirados'}
                    {bulkDeleteFilterType === 'expired_trial' && 'Remove apenas clientes expirados que são teste'}
                    {bulkDeleteFilterType === 'expired_non_trial' && 'Remove apenas clientes expirados que NÃO são teste'}
                    {bulkDeleteFilterType === 'trial' && 'Remove TODOS os clientes teste (independente de expiração)'}
                    {bulkDeleteFilterType === 'expired_days' && 'Remove clientes expirados há X dias ou mais'}
                    {bulkDeleteFilterType === 'expired_days_trial' && 'Remove clientes expirados há X dias ou mais que são Teste'}
                  </p>
                </div>

                {(bulkDeleteFilterType === 'expired_days' || bulkDeleteFilterType === 'expired_days_trial') && (
                  <div className="space-y-2">
                    <Label htmlFor="daysExpired">Dias desde a Expiração</Label>
                    <Input
                      id="daysExpired"
                      type="number"
                      min="0"
                      value={bulkDeleteDaysExpired}
                      onChange={(e) => setBulkDeleteDaysExpired(parseInt(e.target.value) || 0)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {bulkDeleteFilterType === 'expired_days' ? 'Clientes expirados há mais de' : 'Clientes teste expirados há mais de'}  {bulkDeleteDaysExpired} dias serão removidos
                    </p>
                  </div>
                )}
              </div>

              {/* Preview */}
              {bulkDeletePreview ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-blue-900 mb-3">Preview da Remoção</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-blue-700">Total de Clientes:</p>
                      <p className="text-2xl font-bold text-blue-900">{bulkDeletePreview.totalLines}</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-700">Serão Removidos:</p>
                      <p className="text-2xl font-bold text-red-600">{bulkDeletePreview.willDelete}</p>
                    </div>
                  </div>
                  {bulkDeletePreview.samples && bulkDeletePreview.samples.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-semibold text-blue-900 mb-2">Amostras (primeiros 10):</p>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {bulkDeletePreview.samples.map((sample: any) => (
                          <div key={sample.id} className="text-xs text-blue-800 bg-blue-100 p-2 rounded">
                            <strong>{sample.username}</strong> - 
                            {Number(sample.is_trial) > 0 ? ' Teste' : ' Cliente'} - 
                            Expirado há {sample.expiredDays ?? '—'} dias
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600 text-center">
                    Clique em "Fazer Preview" para ver quantos clientes serão removidos
                  </p>
                </div>
              )}

              {/* Ações */}
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBulkDeleteModal(false);
                    setBulkDeletePreview(null);
                  }}
                  disabled={isDeletingBulk}
                >
                  Cancelar
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePreviewBulkDelete}
                  disabled={isLoadingBulkDeletePreview || isDeletingBulk}
                >
                  {isLoadingBulkDeletePreview ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Carregando...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Fazer Preview
                    </>
                  )}
                </Button>
                {bulkDeletePreview && bulkDeletePreview.willDelete > 0 && (
                  <Button
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    disabled={isDeletingBulk}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {isDeletingBulk ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Removendo...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remover {bulkDeletePreview.willDelete} Cliente(s)
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Remoção em Massa */}
      <ConfirmModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={handleExecuteBulkDelete}
        title="Confirmar Remoção em Massa"
        message={`Tem certeza que deseja remover ${bulkDeletePreview?.willDelete || 0} cliente(s)?\n\nCada linha será excluída individualmente (mesmo processo da seleção manual). Esta ação não pode ser desfeita.`}
        type="danger"
        confirmText="Sim, Remover"
        cancelText="Cancelar"
        isLoading={isDeletingBulk}
      />

      {/* Fila: progresso ao remover várias linhas (DELETE /lines/:id sequencial) */}
      {lineDeleteQueue?.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {lineDeleteQueue.running ? 'Removendo linhas…' : 'Remoção concluída'}
            </h3>
            {lineDeleteQueue.truncated && lineDeleteQueue.totalMatching != null && (
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                Foram carregadas {lineDeleteQueue.total} de {lineDeleteQueue.totalMatching} linha(s) encontradas.
                Para processar todas, o limite pode ser aumentado no servidor (BULK_DELETE_QUEUE_MAX_IDS).
              </p>
            )}
            {lineDeleteQueue.running ? (
              <>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-3 overflow-hidden">
                  <div
                    className="bg-red-600 h-3 rounded-full transition-all duration-300"
                    style={{
                      width: `${lineDeleteQueue.total ? (lineDeleteQueue.current / lineDeleteQueue.total) * 100 : 0}%`
                    }}
                  />
                </div>
                <p className="text-sm text-gray-600">
                  {lineDeleteQueue.current} / {lineDeleteQueue.total}
                  {lineDeleteQueue.label ? (
                    <span className="block font-medium text-gray-900 mt-1 truncate" title={lineDeleteQueue.label}>
                      {lineDeleteQueue.label}
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-muted-foreground mt-2">Aguarde — não feche esta página.</p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-700 mb-2">
                  {lineDeleteQueue.succeeded} removida(s)
                  {lineDeleteQueue.failed.length > 0 && (
                    <span className="text-red-600"> — {lineDeleteQueue.failed.length} falha(s)</span>
                  )}
                </p>
                {lineDeleteQueue.failed.length > 0 && (
                  <div className="max-h-36 overflow-y-auto text-xs text-red-900 bg-red-50 rounded p-2 mb-3 space-y-1">
                    {lineDeleteQueue.failed.slice(0, 25).map((f) => (
                      <div key={f.id} className="break-words">
                        <span className="font-mono">{f.id}</span>: {f.message}
                      </div>
                    ))}
                  </div>
                )}
                <Button className="w-full" onClick={() => setLineDeleteQueue(null)}>
                  Fechar
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de Confirmação Moderno */}
      <ConfirmModalComponent />
    </div>
  );
};

export default Clients;
